﻿import {DateOnly, isNullOrEmpty} from "@2d-front-monorepo/core";
import React, {JSX, useCallback, useEffect, useMemo, useRef, useState} from "react";
import Calendar from "../Calendar/Calendar";
import {getBoxModel} from "../helpers/BoxModel";
import {HtmlRequiredPrefixedControlProps} from "./HtmlControlProps";
import LabeledControl from "./LabeledControl";
import {useLabeledControl} from "./LabeledControlData";

export type DatePickerProps = HtmlRequiredPrefixedControlProps<HTMLInputElement> & {
	max?: string;
	min?: string;
	isSelectable?: (date: DateOnly) => boolean;
	dayClassName?: (date: DateOnly) => string | undefined;
};

export default function DatePicker(props: DatePickerProps): JSX.Element {
	const {
		label,
		name,
		value,
		id,
		column,
		disabled,
		hidden,
		onChange,
		onBlur,
		ref,

		required,
		hint,
		error,

		controlPrefix,
		controlSuffix,

		min,
		max,
		isSelectable,
		dayClassName

	} = props;

	const {componentId, ariaRef} = useLabeledControl(id);
	const [currentDate, setCurrentDate] = useState<DateOnly>(() => isNullOrEmpty(value) ? DateOnly.today() : DateOnly.fromIsoString(value));

	const spanRef = useRef<HTMLSpanElement>(null);
	const calendarRef = useRef<HTMLDivElement>(null);

	const handleClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
		e.preventDefault();
		if (!calendarRef.current || !spanRef.current || disabled) {
			return;
		}

		const calendar = calendarRef.current;
		calendar.togglePopover(true);
		const inputBox = getBoxModel(spanRef.current).paddingBox;
		const calendarBox = getBoxModel(calendarRef.current).marginBox;

		let x = inputBox.left + inputBox.width / 2 - calendarBox.width / 2;
		let y = inputBox.top + inputBox.height + 4;

		if (y + calendarBox.height > window.innerHeight) {
			y = inputBox.top - calendarBox.height - 4;
		}

		calendar.style.left = x + "px";
		calendar.style.top = y + "px";
	}, []);

	const calendarSelect = useCallback(function (date: DateOnly): void {
		calendarRef.current?.togglePopover(false);
		const input = spanRef.current?.querySelector("input");
		if (input == null) {
			return;
		}

		input.value = date.toJSON();
		setCurrentDate(date);
		if (!onChange) {
			return;
		}

		const syntheticEvent = new Event("change", {bubbles: true});
		Object.defineProperty(syntheticEvent, "target", {value: input});
		Object.defineProperty(syntheticEvent, "currentTarget", {value: input});
		onChange(syntheticEvent as unknown as React.ChangeEvent<HTMLInputElement>);

	}, [onChange]);

	const [minValue, maxValue] = useMemo(() => {
		const minValue = isNullOrEmpty(min) ? undefined : DateOnly.fromIsoString(min);
		const maxValue = isNullOrEmpty(max) ? undefined : DateOnly.fromIsoString(max);
		return [minValue, maxValue];
	}, [min, max]);

	useEffect(() => {
		if (isNullOrEmpty(value)) {
			return;
		}

		const newDate = DateOnly.fromIsoString(value);
		if (newDate.equals(currentDate)) {
			return;
		}

		setCurrentDate(newDate);
	}, [value, currentDate, setCurrentDate]);

	if (hidden) {
		return <></>;
	}

	return <>
		<LabeledControl label={label}
		                type="date"
		                id={componentId}
		                required={required}
		                column={column}
		                error={error}
		                hint={hint}
		                hidden={hidden}
		                ariaRef={ariaRef}
		                controlPrefix={controlPrefix}
		                controlSuffix={controlSuffix}
		                ref={spanRef}>
			<input
				id={componentId}
				type="date"
				ref={ref}
				onChange={onChange}
				onBlur={onBlur}
				onClick={handleClick}
				name={name}
				value={value}
				max={max}
				min={min}
				readOnly={disabled}
				required={required}
				aria-describedby={ariaRef.current?.describedBy}
				aria-invalid={ariaRef.current?.invalid}
				aria-required={required}
				tabIndex={disabled ? - 1 : undefined}
			/>
		</LabeledControl>
		{!disabled && <div popover="auto" className="popover-calendar" ref={calendarRef}>
			<Calendar selected={currentDate} onSelect={calendarSelect} isSelectable={isSelectable}
			          dayClassName={dayClassName} minValue={minValue} maxValue={maxValue}/>
		</div>}
	</>
}
